import { CircleAlert, KeyRound, Minus, Plus, SearchX, ShieldOff, WalletCards } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, ApiError, type Session } from "./api";
import { demoAccountDetail, demoAccounts } from "./demo";
import type { AccountDetail, AccountSummary } from "./types";
import {
  BackBar,
  DensityToggle,
  Empty,
  Field,
  fmtDate,
  fmtInt,
  fmtRelative,
  fmtSigned,
  humanize,
  Notice,
  PageHead,
  PanelEmpty,
  PanelLoading,
  SearchInput,
  Segmented,
  SkeletonRows,
  StatusDot,
  useIsNarrow,
  useListKeyboard,
  useSearchShortcut,
  type Density
} from "./ui";

type AccountsResponse = { accounts: AccountSummary[]; total: number };

const statusOptions = [
  { value: "", label: "Every status" },
  { value: "active", label: "Active" },
  { value: "pending_activation", label: "Pending activation" },
  { value: "blocked", label: "Blocked" },
  { value: "closed", label: "Closed" }
];

export function AccountsView({
  session,
  demo,
  density,
  onDensityChange
}: {
  session: Session;
  demo: boolean;
  density: Density;
  onDensityChange: (next: Density) => void;
}) {
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [detail, setDetail] = useState<AccountDetail>();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const searchRef = useRef<HTMLInputElement>(null);
  const narrow = useIsNarrow();
  const isNarrow = () => window.matchMedia("(max-width: 900px)").matches;

  useSearchShortcut(searchRef);

  async function load(nextSearch = search, nextStatus = status) {
    setLoading(true);
    setError(undefined);
    try {
      const data = demo
        ? {
            accounts: demoAccounts.filter(
              (account) =>
                `${account.name} ${account.email}`
                  .toLowerCase()
                  .includes(nextSearch.trim().toLowerCase()) &&
                (!nextStatus || account.status === nextStatus)
            ),
            total: demoAccounts.length
          }
        : await api<AccountsResponse>(
            `/api/accounts?${new URLSearchParams({
              search: nextSearch,
              ...(nextStatus ? { status: nextStatus } : {})
            })}`,
            session
          );
      setAccounts(data.accounts);
      setSelectedId((current) =>
        current && data.accounts.some((account) => account.id === current)
          ? current
          : isNarrow()
            ? undefined
            : data.accounts[0]?.id
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Accounts could not be loaded");
    } finally {
      setLoading(false);
    }
  }

  // Debounced search so triage keeps up with typing.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(search, status), search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [search, status, demo]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(undefined);
      return;
    }
    const summary = accounts.find((account) => account.id === selectedId);
    if (demo && summary) {
      setDetail(demoAccountDetail(summary));
      return;
    }
    setDetail(undefined);
    void api<AccountDetail>(`/api/accounts/${selectedId}`, session)
      .then(setDetail)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Account details could not be loaded")
      );
  }, [selectedId, accounts, demo, session]);

  const { onKeyDown, rowProps } = useListKeyboard({
    items: accounts,
    selectedId,
    onSelect: setSelectedId
  });

  function onEscape(event: React.KeyboardEvent) {
    if (event.key !== "Escape") return;
    if (search) setSearch("");
    else if (selectedId) setSelectedId(undefined);
  }

  return (
    <section className="view" onKeyDown={onEscape}>
      <PageHead
        title="Accounts"
        count={loading ? "Loading…" : `${fmtInt(accounts.length)} shown`}
        authority="Account platform"
      >
        <DensityToggle density={density} onChange={onDensityChange} />
      </PageHead>

      <div className="toolbar">
        <SearchInput
          inputRef={searchRef}
          value={search}
          onChange={setSearch}
          label="Search accounts by name or email"
          placeholder="Name or email address"
        />
        <label className="sr-only" htmlFor="account-status">
          Account status
        </label>
        <select
          id="account-status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          style={{ width: "auto", minWidth: 150 }}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="toolbar-note">Balances are whole credits</span>
      </div>

      {error && (
        <Notice
          tone="error"
          className="toolbar-notice"
          action={{ label: "Retry", onClick: () => void load() }}
        >
          {error}
        </Notice>
      )}

      <div className={`split${selectedId ? " has-selection" : ""}`}>
        <div className="split-main">
          <div className="table-wrap" onKeyDown={onKeyDown}>
            <table className="records fixed">
              <colgroup>
                {narrow ? (
                  <>
                    <col style={{ width: "54%" }} />
                    <col style={{ width: "26%" }} />
                    <col style={{ width: "20%" }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "13%" }} />
                  </>
                )}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">{narrow ? "Account" : "Name"}</th>
                  {!narrow && <th scope="col">Email</th>}
                  <th scope="col">Status</th>
                  {!narrow && <th scope="col">Last seen</th>}
                  {!narrow && (
                    <th scope="col" className="right">
                      App
                    </th>
                  )}
                  <th scope="col" className="right">
                    Global
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows columns={narrow ? 3 : 6} />
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id} {...rowProps(account.id)}>
                      <td className="cell-primary">
                        {narrow ? (
                          <span className="cell-stack">
                            <span className="truncate">{account.name}</span>
                            <small>{account.email}</small>
                          </span>
                        ) : (
                          <>
                            {account.name}
                            {account.legacy_source && (
                              <span className="tag">{legacyLabel(account.legacy_source)}</span>
                            )}
                          </>
                        )}
                      </td>
                      {!narrow && <td className="cell-muted">{account.email}</td>}
                      <td>
                        <StatusDot value={account.status} />
                      </td>
                      {!narrow && (
                        <td className="cell-faint">{fmtRelative(account.last_seen_at)}</td>
                      )}
                      {!narrow && (
                        <td className="right num cell-muted">
                          {account.app_balance === "0" ? "—" : fmtInt(account.app_balance)}
                        </td>
                      )}
                      <td
                        className={`right num cell-primary${
                          Number(account.global_balance) < 0 ? " amount-neg" : ""
                        }`}
                      >
                        {fmtInt(account.global_balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {!loading && !accounts.length && (
              <Empty icon={SearchX} title="No account matches this search">
                Try the full email address, or clear the status filter to widen the search.
              </Empty>
            )}
          </div>
        </div>

        <aside className="panel" aria-label="Selected account">
          <BackBar label="All accounts" onBack={() => setSelectedId(undefined)} />
          {detail ? (
            <AccountPanel
              key={detail.account.id}
              detail={detail}
              session={session}
              demo={demo}
              onAuthenticatorReset={(accountId) =>
                // The reset may finish after another account has been opened.
                setDetail((current) =>
                  current?.account.id === accountId
                    ? { ...current, account: { ...current.account, two_factor_enabled: false } }
                    : current
                )
              }
              onAdjusted={(balanceAfter, event) => {
                setDetail((current) =>
                  current
                    ? {
                        ...current,
                        wallets: current.wallets.map((wallet) =>
                          wallet.scope === "global" ? { ...wallet, balance: balanceAfter } : wallet
                        ),
                        events: [event, ...current.events]
                      }
                    : current
                );
                setAccounts((current) =>
                  current.map((account) =>
                    account.id === detail.account.id
                      ? { ...account, global_balance: balanceAfter }
                      : account
                  )
                );
              }}
            />
          ) : selectedId ? (
            <PanelLoading />
          ) : (
            <PanelEmpty icon={WalletCards} title="No account selected">
              Choose a row to see identity, wallet balances, and the credit ledger.
            </PanelEmpty>
          )}
        </aside>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function AccountPanel({
  detail,
  session,
  demo,
  onAdjusted,
  onAuthenticatorReset
}: {
  detail: AccountDetail;
  session: Session;
  demo: boolean;
  onAdjusted: (balanceAfter: string, event: AccountDetail["events"][number]) => void;
  onAuthenticatorReset: (accountId: string) => void;
}) {
  const [mode, setMode] = useState<"edit" | "review">("edit");
  const [direction, setDirection] = useState<"add" | "deduct">("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>();
  const [failure, setFailure] = useState<string>();

  const globalWallet = detail.wallets.find((wallet) => wallet.scope === "global");
  const canAdjust = session.permissions.includes("wallets.adjust") && Boolean(globalWallet);
  const parsedAmount = Number(amount);
  const validAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const signedAmount = direction === "add" ? parsedAmount : -parsedAmount;
  const projected = useMemo(
    () => (globalWallet && validAmount ? Number(globalWallet.balance) + signedAmount : undefined),
    [globalWallet, validAmount, signedAmount]
  );
  const reasonOk = reason.trim().length >= 10;
  const ready = validAmount && reasonOk;

  function touch() {
    setMode("edit");
    setResult(undefined);
    setFailure(undefined);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!globalWallet || !ready) return;
    if (mode === "edit") {
      setMode("review");
      return;
    }
    setBusy(true);
    setFailure(undefined);
    try {
      const response = demo
        ? { balanceAfter: String(Number(globalWallet.balance) + signedAmount) }
        : await api<{ balanceAfter: string }>(
            `/api/accounts/${detail.account.id}/wallet-adjustments`,
            session,
            {
              method: "POST",
              body: JSON.stringify({
                walletId: globalWallet.id,
                amount: String(signedAmount),
                reason: reason.trim(),
                idempotencyKey: crypto.randomUUID()
              })
            }
          );
      onAdjusted(response.balanceAfter, {
        id: `adjustment-${Date.now()}`,
        wallet_id: globalWallet.id,
        event_type: "adjustment",
        amount: String(signedAmount),
        balance_after: response.balanceAfter,
        metadata: { reason: reason.trim() },
        created_at: new Date().toISOString()
      });
      setResult(
        `${direction === "add" ? "Added" : "Deducted"} ${fmtInt(parsedAmount)} credits. Balance is now ${fmtInt(response.balanceAfter)}.`
      );
      setAmount("");
      setReason("");
      setMode("edit");
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : "The adjustment could not be recorded");
      setMode("edit");
    } finally {
      setBusy(false);
    }
  }

  const blindnoteInvolved =
    detail.wallets.some((wallet) => wallet.scope === "application") ||
    detail.migrations?.some((item) => item.source_system === "blindnotegenie");

  return (
    <>
      <div className="panel-scroll">
        <header className="panel-head">
          <div>
            <h2>{detail.account.name}</h2>
            <p>{detail.account.email}</p>
            <code className="id">{detail.account.id}</code>
          </div>
          <StatusDot value={detail.account.status} />
        </header>

        <section className="panel-section">
          <h3>Identity</h3>
          <dl className="facts">
            <div>
              <dt>Email</dt>
              <dd>{detail.account.email_verified ? "Verified" : "Not verified"}</dd>
            </div>
            <div>
              <dt>Multi-factor</dt>
              <dd>{detail.account.two_factor_enabled ? "Enrolled" : "Not enrolled"}</dd>
            </div>
            <div>
              <dt>Origin</dt>
              <dd>
                {detail.account.legacy_source
                  ? legacyLabel(detail.account.legacy_source)
                  : "Evidite"}
              </dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{fmtDate(detail.account.created_at)}</dd>
            </div>
            <div>
              <dt>Last seen</dt>
              <dd>
                {detail.account.last_seen_at ? fmtDate(detail.account.last_seen_at) : "Never"}
              </dd>
            </div>
            <div>
              <dt>Time zone</dt>
              <dd>{detail.account.timezone}</dd>
            </div>
          </dl>
        </section>

        {session.permissions.includes("accounts.manage") && (
          <PasswordResetSection session={session} demo={demo} account={detail.account} />
        )}
        {session.permissions.includes("accounts.manage") && (
          <AuthenticatorResetSection
            session={session}
            demo={demo}
            account={detail.account}
            onReset={onAuthenticatorReset}
          />
        )}

        <section className="panel-section">
          <h3>Wallets</h3>
          {detail.wallets.map((wallet) => (
            <div className="wallet" key={wallet.id}>
              <span className="wallet-name">
                <b>
                  {wallet.scope === "global"
                    ? "Global credits"
                    : (wallet.application_name ?? wallet.application_id)}
                </b>
                <small>
                  {wallet.scope === "global"
                    ? "Spendable across pooled applications"
                    : "Application-only balance"}
                </small>
              </span>
              <span className={`wallet-balance${Number(wallet.balance) < 0 ? " negative" : ""}`}>
                {fmtInt(wallet.balance)}
              </span>
            </div>
          ))}
          {blindnoteInvolved && (
            <Notice tone="warn" className="">
              <b>Blindnote entitlement is translated, not inherited.</b>
              <p>
                {detail.entitlements?.some(
                  (item) => item.application_id === "blindnotegenie" && item.status === "active"
                )
                  ? "Legacy unlimited access is recorded here as an explicit product entitlement."
                  : "Application credits stay separate until a planned 1:1 conversion. Legacy unlimited access becomes an entitlement rather than a balance."}
              </p>
            </Notice>
          )}
        </section>

        <section className="panel-section">
          <h3>Credit ledger</h3>
          <table className="ledger">
            <thead>
              <tr>
                <th scope="col">Event</th>
                <th scope="col">Date</th>
                <th scope="col" className="right">
                  Amount
                </th>
                <th scope="col" className="right">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {mode === "review" && projected !== undefined && (
                <tr className="provisional">
                  <td>Adjustment</td>
                  <td className="date">Not yet recorded</td>
                  <td className={`right ${signedAmount < 0 ? "amount-neg" : "amount-pos"}`}>
                    {fmtSigned(signedAmount)}
                  </td>
                  <td className="right">{fmtInt(projected)}</td>
                </tr>
              )}
              {detail.events.slice(0, 8).map((event) => (
                <tr key={event.id}>
                  <td>{humanize(event.event_type)}</td>
                  <td className="date">{fmtDate(event.created_at)}</td>
                  <td className={`right ${Number(event.amount) < 0 ? "amount-neg" : "amount-pos"}`}>
                    {fmtSigned(event.amount)}
                  </td>
                  <td className="right">{fmtInt(event.balance_after)}</td>
                </tr>
              ))}
              {!detail.events.length && (
                <tr>
                  <td colSpan={4} className="date">
                    No credit movement recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {canAdjust && (
          <section className="panel-section">
            <h3>Adjust global credits</h3>
            <form id="adjust-form" className="stack" onSubmit={(event) => void submit(event)}>
              <div className="row-2">
                <div className="field">
                  <span className="field-label">Direction</span>
                  <Segmented
                    label="Adjustment direction"
                    value={direction}
                    onChange={(next) => {
                      setDirection(next);
                      touch();
                    }}
                    options={[
                      {
                        value: "add",
                        label: (
                          <>
                            <Plus aria-hidden="true" /> Add
                          </>
                        )
                      },
                      {
                        value: "deduct",
                        label: (
                          <>
                            <Minus aria-hidden="true" /> Deduct
                          </>
                        )
                      }
                    ]}
                  />
                </div>
                <Field label="Amount" htmlFor="adjust-amount">
                  <input
                    id="adjust-amount"
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    required
                    value={amount}
                    onChange={(event) => {
                      setAmount(event.target.value);
                      touch();
                    }}
                    placeholder="0"
                  />
                </Field>
              </div>
              <Field
                label="Reason"
                htmlFor="adjust-reason"
                hint={reason.trim().length < 10 ? "10 characters minimum" : `${reason.length}/500`}
              >
                <textarea
                  id="adjust-reason"
                  required
                  minLength={10}
                  maxLength={500}
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                    touch();
                  }}
                  placeholder="What happened, and why is this correction appropriate?"
                />
              </Field>
              {mode === "review" && (
                <Notice tone="warn">
                  This records a permanent ledger entry against {detail.account.name}. Entries
                  cannot be edited or removed later.
                </Notice>
              )}
              {failure && <Notice tone="error">{failure}</Notice>}
              {result && <Notice tone="success">{result}</Notice>}
            </form>
          </section>
        )}
      </div>

      {canAdjust && (
        <footer className="panel-foot">
          {mode === "review" ? (
            <>
              <span className="spacer" />
              <button type="button" className="btn btn-quiet" onClick={() => setMode("edit")}>
                Revise
              </button>
              <button type="submit" form="adjust-form" className="btn btn-primary" disabled={busy}>
                {busy ? "Recording…" : "Confirm adjustment"}
              </button>
            </>
          ) : (
            <>
              <span className="spacer" />
              <button
                type="submit"
                form="adjust-form"
                className="btn btn-primary"
                disabled={!ready}
              >
                <CircleAlert aria-hidden="true" /> Review adjustment
              </button>
            </>
          )}
        </footer>
      )}
    </>
  );
}

/** Sends the account holder the standard "reset your password" email. */
function PasswordResetSection({
  session,
  demo,
  account
}: {
  session: Session;
  demo: boolean;
  account: AccountDetail["account"];
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const eligible = account.status === "active" || account.status === "pending_activation";
  // Accounts that never finished activating get the activation email instead.
  const activation = account.status === "pending_activation";

  async function send() {
    setBusy(true);
    setFailure(undefined);
    try {
      const sent = demo
        ? { template: activation ? "activation" : "reset_password" }
        : await api<{ template?: string }>(
            `/api/accounts/${encodeURIComponent(account.id)}/password-reset`,
            session,
            { method: "POST" }
          );
      // Trust what the platform actually sent over the locally known status.
      setResult(
        `${sent.template === "activation" ? "Activation" : "Reset"} link sent to ${account.email}. It expires in 30 minutes.`
      );
      setConfirming(false);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : "The reset email could not be sent");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel-section">
      <h3>Account access</h3>
      {!eligible ? (
        <p className="cell-muted">
          Password resets are unavailable for {humanize(account.status).toLowerCase()} accounts.
        </p>
      ) : confirming ? (
        <div className="stack">
          <Notice tone="warn">
            {activation
              ? `Email ${account.email} a new link to activate their account and choose a password?`
              : `Email ${account.email} a link to choose a new password? Setting it signs them out everywhere else.`}
          </Notice>
          <div className="row-2">
            <button type="button" className="btn btn-quiet" onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void send()}
            >
              {busy ? "Sending…" : activation ? "Send activation email" : "Send reset email"}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn"
          onClick={() => {
            setResult(undefined);
            setConfirming(true);
          }}
        >
          <KeyRound aria-hidden="true" />{" "}
          {activation ? "Resend activation email…" : "Send password reset…"}
        </button>
      )}
      {failure && <Notice tone="error">{failure}</Notice>}
      {result && <Notice tone="success">{result}</Notice>}
    </section>
  );
}

/**
 * Turns off someone's authenticator app when they've lost it and have no recovery code left
 * (account-platform backlog #91). They sign in with their password afterwards and set it up again.
 */
function AuthenticatorResetSection({
  session,
  demo,
  account,
  onReset
}: {
  session: Session;
  demo: boolean;
  account: AccountDetail["account"];
  onReset: (accountId: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const [reauth, setReauth] = useState(false);
  const own = account.id === session.user.id;
  const staffTarget = account.staff_account === true;
  const allowed = !staffTarget || session.permissions.includes("staff.manage");
  const reasonOk = reason.trim().length >= 10 && reason.trim().length <= 500;

  async function reset(event: FormEvent) {
    event.preventDefault();
    if (!reasonOk) return;
    setBusy(true);
    setFailure(undefined);
    setReauth(false);
    try {
      if (!demo) {
        await api(`/api/accounts/${encodeURIComponent(account.id)}/authenticator-reset`, session, {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim() })
        });
      }
      setResult(
        `Authenticator app turned off for ${account.email}. Their sessions and product sign-ins are revoked (product access already granted runs out within 10 minutes), and a security notice is on its way to them. They sign in with their password and set the app up again.`
      );
      setConfirming(false);
      setReason("");
      onReset(account.id);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "reauth_required") setReauth(true);
      else
        setFailure(
          cause instanceof Error ? cause.message : "The authenticator app could not be reset"
        );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel-section">
      <h3>Authenticator app</h3>
      {result ? null : !account.two_factor_enabled ? (
        <p className="cell-muted">No authenticator app is set up on this account.</p>
      ) : own ? (
        <p className="cell-muted">
          You can't reset your own authenticator app. Ask another staff member.
        </p>
      ) : !allowed ? (
        <p className="cell-muted">
          This is a staff account. Resetting its authenticator app needs the Staff manage
          permission.
        </p>
      ) : confirming ? (
        <form className="stack" onSubmit={(event) => void reset(event)}>
          <Notice tone="warn">
            Turn off the authenticator app for {account.email}? This deletes their app key and
            recovery codes, signs them out everywhere (product access already granted runs out
            within 10 minutes) and emails them. It can't be undone. Until they set the app up again,
            their password alone signs in, so first confirm it's really them.
            {staffTarget &&
              " This is a staff account: you need to have signed in within the last 10 minutes."}
          </Notice>
          <Field
            label="Reason"
            htmlFor="authenticator-reset-reason"
            hint={reason.trim().length < 10 ? "10 characters minimum" : `${reason.length}/500`}
          >
            <textarea
              id="authenticator-reset-reason"
              required
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="How did you confirm who they are, and what happened to their app?"
            />
          </Field>
          <div className="row-2">
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                setReauth(false);
                setFailure(undefined);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-danger" disabled={busy || !reasonOk}>
              {busy ? "Turning off…" : "Turn off authenticator app"}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn"
          onClick={() => {
            setResult(undefined);
            setConfirming(true);
          }}
        >
          <ShieldOff aria-hidden="true" /> Reset authenticator app…
        </button>
      )}
      {reauth && (
        <Notice
          tone="info"
          action={{
            label: "Confirm with sign-in",
            onClick: () => location.assign("/auth/login?reauth=1&return=accounts")
          }}
        >
          <b>Sign in again to reset a staff account's app.</b> It needs a sign-in from the last 10
          minutes. You'll come back to Accounts; open this account and give the reason again.
        </Notice>
      )}
      {failure && <Notice tone="error">{failure}</Notice>}
      {result && <Notice tone="success">{result}</Notice>}
    </section>
  );
}

function legacyLabel(source: string) {
  return source === "blindnotegenie"
    ? "Blindnote"
    : source === "geniecore"
      ? "GenieCore"
      : humanize(source);
}
